/**
 * ShareViewer - Public page for viewing shared snapshots
 *
 * This component handles:
 * - PIN verification
 * - Displaying share content
 * - Downloading artifacts
 *
 * Route: /share/:token
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { shareAPI } from '../../api';
import { useFormat } from '../../i18n/format.js';

// titleKey/messageKey resolved at render — never a translated string baked into a
// module-level constant, it would freeze at the language active on load.
const STATUS_MESSAGES = {
  SHARE_EXPIRED: {
    icon: '⏰',
    titleKey: 'shares.viewer.status.expired.title',
    messageKey: 'shares.viewer.status.expired.message'
  },
  SHARE_REVOKED: {
    icon: '🚫',
    titleKey: 'shares.viewer.status.revoked.title',
    messageKey: 'shares.viewer.status.revoked.message'
  },
  SHARE_NOT_FOUND: {
    icon: '🔍',
    titleKey: 'shares.viewer.status.notFound.title',
    messageKey: 'shares.viewer.status.notFound.message'
  },
  SHARE_LOCKED: {
    icon: '🔒',
    titleKey: 'shares.viewer.status.locked.title',
    messageKey: 'shares.viewer.status.locked.message'
  }
};

export default function ShareViewer({ token: propToken }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  // Token can come from props (simple routing) or URL
  const token = propToken || window.location.pathname.replace('/share/', '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [requiresPin, setRequiresPin] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  const [downloadingArtifact, setDownloadingArtifact] = useState(null);

  useEffect(() => {
    loadShare();
  }, [token]);

  const loadShare = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await shareAPI.getPublic(token);

      if (response.requiresPin) {
        setRequiresPin(true);
        setShareData({ title: response.title, expiresAt: response.expiresAt });
      } else if (response.code) {
        // Error response
        setError(response.code);
      } else {
        setShareData(response);
        setRequiresPin(false);
      }
    } catch (err) {
      setError('NETWORK_ERROR');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinLoading(true);
    setPinError(null);

    try {
      const unlockResponse = await shareAPI.unlock(token, pinValue);

      if (unlockResponse.success) {
        // PIN verified, reload share data
        await loadShare();
      } else {
        setPinError(unlockResponse.error || t('shares.viewer.pin.incorrect'));
        if (unlockResponse.attemptsRemaining !== undefined) {
          setPinError(t('shares.viewer.pin.incorrectWithAttempts', { count: unlockResponse.attemptsRemaining }));
        }
        if (unlockResponse.code === 'SHARE_LOCKED') {
          setError('SHARE_LOCKED');
        }
      }
    } catch (err) {
      setPinError(t('shares.viewer.pin.networkError'));
    } finally {
      setPinLoading(false);
    }
  };

  const handleDownload = async (artifact) => {
    setDownloadingArtifact(artifact.name);
    try {
      await shareAPI.downloadArtifact(token, artifact.name);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingArtifact(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">{t('common.states.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    const errorInfo = STATUS_MESSAGES[error] || {
      icon: '❌',
      titleKey: 'common.messages.errorTitle',
      messageKey: 'shares.viewer.status.generic.message'
    };

    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">{errorInfo.icon}</div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">{t(errorInfo.titleKey)}</h1>
          <p className="text-slate-600">{t(errorInfo.messageKey)}</p>
        </div>
      </div>
    );
  }

  // PIN required state
  if (requiresPin) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 text-3xl mb-4">
              🔐
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              {t('shares.viewer.pin.title')}
            </h1>
            {shareData?.title && (
              <p className="text-slate-600 font-medium">{shareData.title}</p>
            )}
            <p className="text-sm text-slate-500 mt-1">
              {t('shares.viewer.pin.subtitle')}
            </p>
          </div>

          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <label className="sr-only">{t('shares.viewer.pin.label')}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {pinError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                {pinError}
              </div>
            )}

            <button
              type="submit"
              disabled={pinLoading || pinValue.length !== 6}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {pinLoading ? t('shares.viewer.pin.verifying') : t('shares.viewer.pin.access')}
            </button>
          </form>

          {shareData?.expiresAt && (
            <p className="text-xs text-slate-400 text-center mt-4">
              {t('shares.viewer.pin.expiresOn', { date: fmt.date(shareData.expiresAt) })}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Main share view
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">{shareData.title}</h1>
              <p className="text-sm text-slate-500">
                {t('shares.viewer.main.snapshotOf', {
                  date: fmt.date(shareData.asOf, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                })}
              </p>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                shareData.status === 'ACTIVE'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-slate-100 text-slate-800'
              }`}>
                {shareData.status === 'ACTIVE' ? t('shares.status.active') : shareData.status}
              </span>
              <p className="text-xs text-slate-400 mt-1">
                {t('shares.success.expiresOn', { date: fmt.date(shareData.expiresAt) })}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Watermark Banner */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-4xl mx-auto px-4 py-2">
          <p className="text-sm text-amber-800 flex items-center gap-2">
            <span>📋</span>
            <span>{t('shares.viewer.main.readOnlyBanner', { watermark: shareData.watermark })}</span>
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* View count */}
        <div className="text-sm text-slate-500 mb-4">
          {t('shares.viewer.main.viewedCount', { count: shareData.viewCount || 1 })}
        </div>

        {/* Artifacts */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h2 className="font-medium text-slate-800">{t('shares.viewer.main.artifactsTitle')}</h2>
          </div>
          <ul className="divide-y divide-slate-200">
            {shareData.artifacts?.map((artifact, index) => (
              <li key={index} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">
                    {artifact.kind === 'pdf' ? '📄' :
                     artifact.kind === 'csv' ? '📊' :
                     artifact.kind === 'html' ? '🌐' : '📎'}
                  </span>
                  <div>
                    <p className="font-medium text-slate-800">{artifact.name}</p>
                    <p className="text-sm text-slate-500">
                      {t('shares.viewer.main.artifactMeta', { mimeType: artifact.mimeType, size: formatBytes(artifact.sizeBytes) })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(artifact)}
                  disabled={downloadingArtifact === artifact.name}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {downloadingArtifact === artifact.name ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('shares.viewer.main.downloading')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {t('common.actions.download')}
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Scope info (if available) */}
        {shareData.scope && Object.keys(shareData.scope).length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <h2 className="font-medium text-slate-800">{t('shares.viewer.main.scopeTitle')}</h2>
            </div>
            <div className="px-4 py-3 text-sm text-slate-600">
              <pre className="font-mono text-xs bg-slate-50 p-2 rounded">
                {JSON.stringify(shareData.scope, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-8">
        <div className="max-w-4xl mx-auto px-4 py-4 text-center text-sm text-slate-500">
          {t('shares.viewer.footer')}
        </div>
      </footer>
    </div>
  );
}

// Helper function
function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
